import { FaExclamationTriangle } from 'react-icons/fa';

const formatDate = (iso) => {
  if (!iso) return null;
  const date = new Date(`${iso}T00:00:00`);
  return isNaN(date.getTime())
    ? iso
    : date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
};

/**
 * Confirmation shown before saving a schedule whose dates changed.
 *
 * Saving deletes every uncompleted job on the schedule and regenerates from the new dates — so
 * jobs dated before the new start date are removed and never come back. The counts come from the
 * backend preview so they match exactly what the save will do.
 */
const RegenerateJobsWarningModal = ({
  isOpen,
  loading,
  preview,
  error,
  newStartDate,
  submitting,
  onConfirm,
  onCancel,
}) => {
  if (!isOpen) return null;

  const total = preview?.total ?? 0;
  const lost = preview?.beforeNewStart ?? 0;
  const kept = preview?.afterNewStart ?? 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-full overflow-y-auto">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center mb-4">
            <FaExclamationTriangle className="text-amber-500 mr-2 flex-shrink-0" />
            Changing these dates rebuilds the jobs
          </h3>

          {loading && (
            <p className="text-sm text-gray-600 py-4">Checking which jobs are affected…</p>
          )}

          {!loading && error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3 mb-4">
              <p className="m-0">Could not check the affected jobs: {error}</p>
              <p className="m-0 mt-2">
                Saving will still delete every uncompleted job on this schedule and regenerate from
                the new dates. This cannot be undone.
              </p>
            </div>
          )}

          {!loading && !error && (
            <div className="space-y-3 mb-4">
              <div className="border border-amber-300 bg-amber-50 rounded-md p-4 text-sm text-amber-900">
                {total === 0 ? (
                  <p className="m-0">
                    No uncompleted jobs to remove. New jobs will be generated from the updated dates.
                  </p>
                ) : (
                  <p className="m-0">
                    All <strong>{total}</strong> uncompleted job{total === 1 ? '' : 's'} on this
                    schedule will be deleted and regenerated from the new dates.
                    {preview?.earliest && preview?.latest && (
                      <> They currently run {formatDate(preview.earliest)} – {formatDate(preview.latest)}.</>
                    )}
                  </p>
                )}
              </div>

              {lost > 0 && (
                <div className="border border-red-300 bg-red-50 rounded-md p-4 text-sm text-red-900">
                  <p className="m-0">
                    <strong>{lost}</strong> of them {lost === 1 ? 'is' : 'are'} dated before
                    {newStartDate ? <> the new start date, {formatDate(newStartDate)}</> : <> the new start date</>}.
                    {lost === 1 ? ' It' : ' They'} will <strong>not</strong> be regenerated —
                    {lost === 1 ? ' it is' : ' they are'} permanently deleted. This cannot be undone.
                  </p>
                  {kept > 0 && (
                    <p className="m-0 mt-2">The other {kept} will be recreated on the new dates.</p>
                  )}
                </div>
              )}

              <p className="text-xs text-gray-500 m-0">
                Completed and invoiced jobs are never touched.
              </p>
            </div>
          )}

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onCancel}
              disabled={submitting}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 border border-gray-300 rounded-md shadow-sm hover:bg-gray-300 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={submitting || loading}
              className={`px-4 py-2 text-sm font-medium text-white border rounded-md shadow-sm disabled:opacity-50 ${
                lost > 0
                  ? 'bg-red-600 border-red-700 hover:bg-red-700'
                  : 'bg-amber-500 border-amber-600 hover:bg-amber-600'
              }`}
            >
              {submitting ? 'Saving…' : lost > 0 ? `Delete ${lost} and save` : 'Save changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegenerateJobsWarningModal;
